import { AMBIGUS, CORR_DICT, corrigeTout } from './correcteur';

/**
 * Corrige l'écriture d'un texte dicté ou tapé dans l'assistant (offre et
 * facture), sans rien changer d'autre : ni les nombres, ni les virgules, ni
 * les retours à la ligne.
 *
 * <p>« L'assistant vocal ne corrige pas l'orthographe quand on prononce mal :
 * tu ne touches à rien, c'est juste corriger l'écriture. » Le téléphone
 * écrit ce qu'il croit entendre ; l'assistant le reprenait tel quel. Trois
 * corrections, toutes locales (rien ne part sur le réseau) :
 *
 * <ol>
 *   <li>les fautes connues (`CORR_DICT` : « faillance » → « faïence ») ;</li>
 *   <li>les accents oubliés d'un mot du métier ou du catalogue, écrit sans
 *       aucun accent (« facade » → « façade », « etancheite » → « étanchéité ») ;</li>
 *   <li>une lettre doublée ou oubliée dans un mot d'au moins six lettres
 *       (« carrellage » → « carrelage », « instalation » → « installation »).</li>
 * </ol>
 *
 * <p>Un mot n'est corrigé que s'il ne peut l'être qu'en un seul mot : jamais
 * de devinette. Les mots courts ou qui s'écrivent juste de deux façons
 * (« tache » / « tâche », « sale » / « salle ») ne sont pas dans la liste.
 */

/** Les mots du métier, bien écrits. */
const MOTS_DU_METIER = [
  'façade',
  'crépi',
  'crépissage',
  'chape',
  'plâtre',
  'plâtrerie',
  'gypserie',
  'faïence',
  'carrelage',
  'carreleur',
  'plinthe',
  'échafaudage',
  'étanchéité',
  'étanche',
  'peinture',
  'enduit',
  'ragréage',
  'cloison',
  'plafond',
  'isolation',
  'démolition',
  'évacuation',
  'mortier',
  'béton',
  'parquet',
  'stratifié',
  'moquette',
  'lambris',
  'tuile',
  'gouttière',
  'chéneau',
  'ferblanterie',
  'menuiserie',
  'vitrage',
  'fenêtre',
  'baignoire',
  'lavabo',
  'évier',
  'robinet',
  'robinetterie',
  'douche',
  'carreau',
  'silicone',
  'primaire',
  'accrochage',
  'maçonnerie',
  'électricité',
  'électrique',
  'câblage',
  'interrupteur',
  'luminaire',
  'chauffage',
  'radiateur',
  'chaudière',
  'sanitaire',
  'ventilation',
  'désamiantage',
  'nettoyage',
  'débarras',
  'déplacement',
  'protection',
  'bâche',
  'nacelle',
  'déchets',
  'décharge',
  'réfection',
  'rénovation',
  'réparation',
  'remplacement',
  'fourniture',
  'installation',
  'mélèze',
  'épicéa',
  'chêne',
  'hêtre',
  'mélaminé',
  'contreplaqué',
  'aggloméré',
  'polystyrène',
  'minérale',
  'pare-vapeur',
  'tôle',
  'dépôt',
  'préparation',
  'ponçage',
  'égrenage',
  'lasure',
  'vernis',
  'rebouchage',
  'fissure',
  'humidité',
  'moisissure',
  'intérieur',
  'extérieur',
  'matériaux',
  'matériel',
  'escalier',
  'balcon',
  'terrasse',
  'véranda',
  'toiture',
  'charpente',
  'isolant',
  'colle',
  'joint',
  'jointoiement',
];

/** Les mots justes du dictionnaire, sauf les formes de verbe (« livre » / « livré »). */
const JUSTES_DU_DICTIONNAIRE = Object.entries(CORR_DICT)
  .filter(([faute]) => !AMBIGUS.has(faute))
  .map(([, juste]) => juste);

/** Minuscules, sans accents. */
const sansAccent = (x) =>
  String(x || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/** « carrellage » et « carrelage » deviennent la même clé. */
const sansDouble = (x) => x.replace(/(\p{L})\1+/gu, '$1');

/** Un mot porte-t-il un accent ou une cédille ? */
const accentue = (x) => sansAccent(x) !== String(x).toLowerCase();

/** Les mots du catalogue (« Carrelage 30×60 » → « carrelage »). */
function motsDuCatalogue(catalogue) {
  const mots = [];
  for (const a of catalogue || []) {
    for (const m of String(a?.nom || '').split(/[^\p{L}'-]+/u)) {
      if (m.length >= 5) mots.push(m.toLowerCase());
    }
  }
  return mots;
}

/**
 * Les deux tables de correction : par accents (mot de 4 lettres et plus qui
 * porte un accent) et par lettres doublées (mot de 6 lettres et plus). Une
 * clé qui mène à deux mots différents est retirée : on ne devine pas.
 */
function tables(catalogue) {
  const parAccent = new Map();
  const parDouble = new Map();
  const ajoute = (table, cle, mot) => {
    if (!table.has(cle)) table.set(cle, mot);
    else if (table.get(cle) !== mot) table.set(cle, null);
  };
  const remplir = (mots) => {
    for (const mot of mots) {
      const cle = sansAccent(mot);
      if (cle.length >= 4 && accentue(mot)) ajoute(parAccent, cle, mot);
      if (cle.length >= 6) ajoute(parDouble, sansDouble(cle), mot);
    }
  };
  remplir([...MOTS_DU_METIER, ...JUSTES_DU_DICTIONNAIRE]);
  // Le catalogue complète la liste sans jamais la contredire.
  const metier = { parAccent: new Map(parAccent), parDouble: new Map(parDouble) };
  for (const mot of motsDuCatalogue(catalogue)) {
    const cle = sansAccent(mot);
    if (cle.length >= 4 && accentue(mot) && !metier.parAccent.has(cle)) ajoute(parAccent, cle, mot);
    if (cle.length >= 6 && !metier.parDouble.has(sansDouble(cle))) {
      ajoute(parDouble, sansDouble(cle), mot);
    }
  }
  return { parAccent, parDouble };
}

/** Garde la majuscule initiale (« Facade » → « Façade »). */
const memeCasse = (trouve, juste) =>
  trouve.charAt(0) !== trouve.charAt(0).toLowerCase()
    ? juste.charAt(0).toUpperCase() + juste.slice(1)
    : juste;

/** Le bon mot pour `mot`, ou null s'il n'y a rien à corriger. */
function bonMot(mot, { parAccent, parDouble }) {
  // Le téléphone écrit des mots justes, accents compris : un mot qui porte
  // déjà un accent (« crée », « déchargé ») n'est jamais réaccentué.
  const sansAucunAccent = !accentue(mot);
  const essai = (forme, fin) => {
    const cle = sansAccent(forme);
    const parA = sansAucunAccent ? parAccent.get(cle) : null;
    if (parA) return parA + fin;
    if (cle.length >= 6) {
      const parD = parDouble.get(sansDouble(cle));
      // Seulement une vraie lettre en trop ou en moins : « déchargé » n'est
      // pas une faute de « décharge ».
      if (parD && sansAccent(parD) !== cle) return parD + fin;
    }
    return null;
  };
  const bas = mot.toLowerCase();
  let juste = essai(mot, '');
  // Un pluriel (« carrellages », « facades ») garde son s ou son x.
  if (!juste && /[sx]$/.test(bas) && bas.length > 4) juste = essai(mot.slice(0, -1), bas.slice(-1));
  if (!juste || juste === bas) return null;
  return memeCasse(mot, juste);
}

/**
 * Corrige l'écriture du texte de l'assistant.
 *
 * @param {string} texte texte dicté ou tapé
 * @param {object[]} [catalogue] articles de l'entreprise
 * @returns {{texte: string, corrections: [string, string][]}} le texte
 *   corrigé et les mots remplacés (avant, après)
 */
export function corrigeLaDictee(texte, catalogue) {
  if (!texte) return { texte, corrections: [] };
  const premier = corrigeTout(texte);
  const corrections = [...premier.corrections];
  const t = tables(catalogue);
  const sortie = premier.texte.replace(/\p{L}+(?:-\p{L}+)*/gu, (mot) => {
    const juste = bonMot(mot, t);
    if (!juste) return mot;
    if (!corrections.some(([a]) => a === mot)) corrections.push([mot, juste]);
    return juste;
  });
  return { texte: sortie, corrections };
}

/**
 * Parmi les phrases que le téléphone propose pour une même dictée, la plus
 * juste : celle qui contient le plus de mots du métier ou du catalogue (les
 * longs comptent plus), une fois leur écriture corrigée. À
 * égalité, la première (celle que le téléphone juge la plus probable).
 *
 * @param {string[]} propositions phrases proposées, la plus probable d'abord
 * @param {object[]} [catalogue] articles de l'entreprise
 * @returns {string} la phrase retenue ('' s'il n'y en a aucune)
 */
export function meilleureDictee(propositions, catalogue) {
  const liste = (propositions || []).filter((p) => p && p.trim());
  if (liste.length <= 1) return liste[0] || '';
  const connus = new Set(
    [...MOTS_DU_METIER, ...JUSTES_DU_DICTIONNAIRE, ...motsDuCatalogue(catalogue)].map((m) =>
      sansAccent(m),
    ),
  );
  // Un long mot du métier (« carrelage ») pèse plus qu'un mot court (« carré »).
  const score = (p) =>
    (sansAccent(corrigeLaDictee(p, catalogue).texte).match(/\p{L}+(?:-\p{L}+)*/gu) || [])
      .filter((m) => connus.has(m))
      .reduce((total, m) => total + m.length, 0);
  let meilleure = liste[0];
  let meilleurScore = score(meilleure);
  for (const p of liste.slice(1)) {
    const s = score(p);
    if (s > meilleurScore) {
      meilleure = p;
      meilleurScore = s;
    }
  }
  return meilleure;
}
